// ============================================================
// FAKTURA KAO PRAVI PDF (tekst, ne slika) — za prilog uz mejl
// Crta se jsPDF-om sa ugrađenim fontom (Plus Jakarta Sans; za ruski Noto Sans),
// pa se tekst može označiti i kopirati, kao kod "Sačuvaj kao PDF".
// Fontovi su u public/fonts, a jsPDF se učitava tek kad zatreba.
// ============================================================

const JSPDF_URL = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';

let jspdfPromise = null;
function loadJsPdf() {
  if (window.jspdf?.jsPDF) return Promise.resolve(window.jspdf.jsPDF);
  if (!jspdfPromise) {
    jspdfPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = JSPDF_URL;
      s.async = true;
      s.onload = () => resolve(window.jspdf.jsPDF);
      s.onerror = () => { jspdfPromise = null; reject(new Error('Alat za PDF nije mogao da se učita. Proverite internet vezu.')); };
      document.head.appendChild(s);
    });
  }
  return jspdfPromise;
}

const FONT_SETS = {
  latin: { regular: '/fonts/PlusJakartaSans-Regular.ttf', bold: '/fonts/PlusJakartaSans-Bold.ttf', heavy: '/fonts/PlusJakartaSans-ExtraBold.ttf' },
  cyrillic: { regular: '/fonts/NotoSans-Regular.ttf', bold: '/fonts/NotoSans-Bold.ttf', heavy: '/fonts/NotoSans-Bold.ttf' },
};
const fontCache = {};
async function fontBase64(url) {
  if (!fontCache[url]) {
    fontCache[url] = fetch(url).then((r) => {
      if (!r.ok) throw new Error(`Font nije pronađen (${url}). Proverite fasciklu public/fonts.`);
      return r.arrayBuffer();
    }).then((buf) => {
      let bin = '';
      const bytes = new Uint8Array(buf);
      for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
      return btoa(bin);
    }).catch((e) => { delete fontCache[url]; throw e; });
  }
  return fontCache[url];
}

// Logo u PNG (radi i za SVG/WebP) i njegova veličina
function imageToPng(src) {
  return new Promise((resolve) => {
    if (!src) { resolve(null); return; }
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth || 600;
      const h = img.naturalHeight || 300;
      const scale = Math.min(1, 1200 / Math.max(w, h));
      const c = document.createElement('canvas');
      c.width = Math.round(w * scale);
      c.height = Math.round(h * scale);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      resolve({ data: c.toDataURL('image/png'), w: c.width, h: c.height });
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

const C = { ink: '#17242B', grey: '#4A5960', soft: '#5F6B71', teal: '#12343F', line: '#E7E6E1', paper: '#F6F6F3', red: '#A12828', gold: '#8A5A0B' };
const PT = 0.3528; // 1 pt u mm

/**
 * data: {
 *   lang, fileTitle, title, number,
 *   logo, issuerName, issuerLines: [{ label?, value, bold? }],
 *   meta: [[label, value]],
 *   forLabel, customerName, customerLines: [string],
 *   cols: { item, unit, qty, price, discount, total }, hasDiscount,
 *   items: [{ name, unit, qty, price, discount, total }],
 *   noteTitle, vat, totalLabel, totalValue, dueLabel, dueValue,
 *   stornoText?, proformaText?,
 *   issuedByLabel, signer, messageLabel, message,
 *   qrDataUrl, footer
 * }
 * Vraća jsPDF dokument (pozivalac bira .output('bloburl' | 'datauristring')).
 */
export async function buildInvoicePdf(data) {
  const JsPDF = await loadJsPdf();
  const set = data.lang === 'ru' ? FONT_SETS.cyrillic : FONT_SETS.latin;
  const [reg, bold, heavy] = await Promise.all([fontBase64(set.regular), fontBase64(set.bold), fontBase64(set.heavy)]);
  const logo = await imageToPng(data.logo);

  const doc = new JsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });
  doc.addFileToVFS('F-Regular.ttf', reg); doc.addFont('F-Regular.ttf', 'F', 'normal');
  doc.addFileToVFS('F-Bold.ttf', bold); doc.addFont('F-Bold.ttf', 'F', 'bold');
  doc.addFileToVFS('F-Heavy.ttf', heavy); doc.addFont('F-Heavy.ttf', 'FH', 'normal');
  doc.setProperties({ title: data.fileTitle || data.title, creator: 'FinArt Studio' });
  doc.setLineHeightFactor(1.35);

  const W = 210, H = 297, M = 16, R = W - M;
  const lh = (size) => size * PT * 1.42;
  const font = (style = 'normal', size = 9.5, color = C.ink) => {
    if (style === 'heavy') doc.setFont('FH', 'normal'); else doc.setFont('F', style);
    doc.setFontSize(size);
    doc.setTextColor(color);
  };
  // Tekst sa prelamanjem; vraća novu y poziciju (ispod teksta)
  const para = (text, x, y, width, size, style, color, align = 'left') => {
    font(style, size, color);
    const lines = doc.splitTextToSize(String(text ?? ''), width);
    lines.forEach((ln, i) => {
      const yy = y + size * PT + i * lh(size);
      if (align === 'right') doc.text(ln, x + width, yy, { align: 'right' });
      else if (align === 'center') doc.text(ln, x + width / 2, yy, { align: 'center' });
      else doc.text(ln, x, yy);
    });
    return y + lines.length * lh(size);
  };
  const hline = (y, x1 = M, x2 = R) => { doc.setDrawColor(C.line); doc.setLineWidth(0.25); doc.line(x1, y, x2, y); };

  // ---------- Zaglavlje: izdavalac levo, dokument desno ----------
  const leftW = 104;
  const rightX = 126;
  let yL = M;
  if (logo) {
    const maxW = 52, maxH = 20;
    const k = Math.min(maxW / (logo.w * 0.2646), maxH / (logo.h * 0.2646), 1);
    const lw = logo.w * 0.2646 * k, lhgt = logo.h * 0.2646 * k;
    doc.addImage(logo.data, 'PNG', M, yL, lw, lhgt);
    yL += lhgt + 3;
  }
  yL = para(data.issuerName, M, yL, leftW, 10.5, 'heavy', C.ink);
  yL += 0.6;
  (data.issuerLines || []).forEach((ln) => {
    if (ln.label) {
      font('normal', 9, C.ink);
      const lab = `${ln.label}: `;
      const lw = doc.getTextWidth(lab);
      doc.text(lab, M, yL + 9 * PT);
      font(ln.bold ? 'bold' : 'normal', 9, C.ink);
      const rest = doc.splitTextToSize(String(ln.value), leftW - lw);
      rest.forEach((r, i) => doc.text(r, M + lw, yL + 9 * PT + i * lh(9)));
      yL += rest.length * lh(9);
    } else {
      yL = para(ln.value, M, yL, leftW, 9, 'normal', C.ink);
    }
  });

  let yR = M + 1;
  yR = para(data.title, rightX, yR, R - rightX, 15.5, 'heavy', C.ink);
  if (data.number) yR = para(data.number, rightX, yR, R - rightX, 11, 'bold', C.teal);
  yR += 2.5;
  (data.meta || []).forEach(([label, value]) => {
    font('normal', 8.8, C.grey);
    doc.text(String(label), rightX, yR + 8.8 * PT);
    font('bold', 8.8, C.ink);
    doc.text(String(value), rightX + 31, yR + 8.8 * PT);
    yR += lh(8.8) + 0.6;
  });

  let y = Math.max(yL, yR) + 5;
  hline(y);
  y += 6;

  // ---------- Kupac ----------
  y = para(data.forLabel, M, y, 120, 8.8, 'normal', C.soft);
  y += 1.2;
  y = para(data.customerName, M, y, 140, 11, 'heavy', C.ink);
  (data.customerLines || []).forEach((t) => { y = para(t, M, y, 140, 9.2, 'normal', C.ink); });
  y += 6;

  // ---------- Stavke ----------
  const cols = [];
  let xr = R;
  const addCol = (key, width, align) => { cols.unshift({ key, width, align, x: xr - width }); xr -= width; };
  addCol('total', 32, 'right');
  if (data.hasDiscount) addCol('discount', 16, 'right');
  addCol('price', 26, 'right');
  addCol('qty', 17, 'right');
  addCol('unit', 21, 'left');
  cols.unshift({ key: 'name', width: xr - M, align: 'left', x: M });
  const pad = 2.2;

  const headerH = lh(8.6) + 5;
  hline(y);
  cols.forEach((c) => {
    const label = c.key === 'name' ? data.cols.item : data.cols[c.key];
    font('bold', 8.6, C.ink);
    const lines = doc.splitTextToSize(String(label), c.width - pad * 2);
    lines.forEach((ln, i) => {
      const yy = y + 2.5 + 8.6 * PT + i * lh(8.6);
      if (c.align === 'right') doc.text(ln, c.x + c.width - pad, yy, { align: 'right' });
      else doc.text(ln, c.x + pad, yy);
    });
  });
  y += headerH;
  hline(y);

  (data.items || []).forEach((it) => {
    font('normal', 9, C.ink);
    const nameLines = doc.splitTextToSize(String(it.name || '—'), cols[0].width - pad * 2);
    const rowH = Math.max(1, nameLines.length) * lh(9) + 5;
    if (y + rowH > H - 60) { doc.addPage(); y = M; }
    cols.forEach((c) => {
      const yy = y + 2.5 + 9 * PT;
      if (c.key === 'name') {
        font('normal', 9, C.ink);
        nameLines.forEach((ln, i) => doc.text(ln, c.x + pad, yy + i * lh(9)));
        return;
      }
      const val = String(it[c.key] ?? '');
      font(c.key === 'total' ? 'bold' : 'normal', 9, C.ink);
      if (c.align === 'right') doc.text(val, c.x + c.width - pad, yy, { align: 'right' });
      else doc.text(doc.splitTextToSize(val, c.width - pad * 2)[0] || '', c.x + pad, yy);
    });
    y += rowH;
    hline(y);
  });

  // ---------- Napomena (PDV) i zbir ----------
  y += 5;
  const noteW = 96;
  let yn = para(data.noteTitle, M, y, noteW, 9, 'bold', C.ink);
  yn = para(data.vat, M, yn, noteW, 9, 'normal', C.ink);

  const tx = 120, tw = R - tx;
  let yt = y;
  font('normal', 9.2, C.ink);
  doc.text(String(data.totalLabel), tx + 2.5, yt + 9.2 * PT);
  font('bold', 9.2, C.ink);
  doc.text(String(data.totalValue), R - 2.5, yt + 9.2 * PT, { align: 'right' });
  yt += lh(9.2) + 2.2;
  const boxH = lh(10.5) + 4.4;
  doc.setFillColor(C.paper);
  doc.roundedRect(tx, yt, tw, boxH, 1.6, 1.6, 'F');
  font('heavy', 10.5, C.ink);
  doc.text(String(data.dueLabel), tx + 2.5, yt + 2.2 + 10.5 * PT);
  doc.text(String(data.dueValue), R - 2.5, yt + 2.2 + 10.5 * PT, { align: 'right' });
  yt += boxH;

  y = Math.max(yn, yt) + 4;

  const notice = (text, bg, color, style) => {
    font(style, 9, color);
    const lines = doc.splitTextToSize(String(text), R - M - 8);
    const h = lines.length * lh(9) + 5;
    doc.setFillColor(bg);
    doc.roundedRect(M, y, R - M, h, 1.6, 1.6, 'F');
    lines.forEach((ln, i) => doc.text(ln, M + 4, y + 2.5 + 9 * PT + i * lh(9)));
    y += h + 3;
  };
  if (data.stornoText) notice(data.stornoText, '#FDECEC', C.red, 'bold');
  if (data.proformaText) notice(data.proformaText, '#F6EEDC', C.gold, 'normal');

  // ---------- Fakturisao, poruka i QR ----------
  y += 2;
  hline(y);
  y += 5;
  const qrSize = 32;
  const leftBottomW = data.qrDataUrl ? R - M - qrSize - 10 : R - M;
  let yb = y;
  if (data.qrDataUrl) {
    doc.addImage(data.qrDataUrl, 'PNG', R - qrSize, y, qrSize, qrSize);
    font('bold', 7, C.ink);
    doc.text('NBS IPS QR', R - qrSize / 2, y + qrSize + 3.2, { align: 'center' });
    yb = y + qrSize + 4;
  }
  let ys = y + (data.qrDataUrl ? 12 : 0);
  ys = para(data.issuedByLabel, M, ys, leftBottomW, 9.5, 'normal', C.ink);
  ys = para(data.signer || '—', M, ys, leftBottomW, 11, 'bold', C.ink);
  if (data.message) {
    ys += 4;
    ys = para(data.messageLabel, M, ys, leftBottomW, 8.8, 'normal', C.soft);
    ys = para(data.message, M, ys + 0.8, leftBottomW, 9.2, 'normal', C.ink);
  }
  y = Math.max(yb, ys);

  // ---------- Tekst o važenju računa: uvek na dnu strane ----------
  font('normal', 8, C.grey);
  const fLines = doc.splitTextToSize(String(data.footer), R - M - 10);
  const fH = fLines.length * lh(8);
  let fy = H - 12 - fH;
  if (y + 6 > fy - 4) { doc.addPage(); fy = H - 12 - fH; }
  hline(fy - 4);
  fLines.forEach((ln, i) => doc.text(ln, W / 2, fy + 8 * PT + i * lh(8), { align: 'center' }));

  return doc;
}
